import { SITE } from "@/content/site";

/**
 * Shared schema.org builders.
 *
 * Routes used to hand-write these object literals inline; keeping them here
 * means one shape per schema type and one place to fix a mistake. Output is
 * intentionally byte-identical to the literals it replaced.
 */

export type Faq = { q: string; a: string };

export const abs = (path: string) =>
  path.startsWith("http") ? path : `${SITE.url}${path === "/" ? "" : path}`;

/** `mainEntity` array for a FAQPage — useful when embedding in a bigger graph. */
export function faqEntities(items: Faq[], nameSuffix?: string) {
  return items.map((f) => ({
    "@type": "Question",
    name: nameSuffix ? `${f.q} (${nameSuffix})` : f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  }));
}

export function faqSchema(items: Faq[], nameSuffix?: string) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqEntities(items, nameSuffix),
  };
}

export function itemListSchema(
  items: Array<{ name: string; path: string }>,
  extra?: Record<string, unknown>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    ...extra,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: abs(it.path),
      name: it.name,
    })),
  };
}

export function collectionSchema(input: {
  name: string;
  path: string;
  description: string;
  items: Array<{ name: string; path: string }>;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: input.name,
    url: abs(input.path),
    description: input.description,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: input.items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: abs(it.path),
        name: it.name,
      })),
    },
  };
}

export function howToSchema(input: {
  name: string;
  description?: string;
  steps: Array<{ name: string; text: string }>;
  extra?: Record<string, unknown>;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: input.name,
    ...(input.description ? { description: input.description } : {}),
    ...(input.extra ?? {}),
    step: input.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

/**
 * LocalBusiness (ProfessionalService) node for a city landing page.
 * Shared by the city hub and every city × service page so the same
 * business entity is described consistently, with a page-scoped @id.
 */
export function cityLocalBusinessSchema(input: {
  city: string;
  region: string;
  country: string;
  countryCode: string;
  geo: { lat: number; lng: number };
  path: string;
  name?: string;
  description?: string;
  serviceType?: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${abs(input.path)}#business`,
    name: input.name ?? `Freelancer Tamal — SEO Expert in ${input.city}`,
    description:
      input.description ??
      `Local SEO, technical SEO, and digital marketing services for businesses in ${input.city}, ${input.country}.`,
    url: abs(input.path),
    image: abs("/og-image.jpg"),
    telephone: SITE.phone,
    email: SITE.email,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      addressLocality: input.city,
      addressRegion: input.region,
      addressCountry: input.countryCode,
    },
    geo: { "@type": "GeoCoordinates", latitude: input.geo.lat, longitude: input.geo.lng },
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: SITE.phone,
        contactType: "sales",
        email: SITE.email,
        areaServed: [input.countryCode, "Worldwide"],
        availableLanguage: ["English", "Bengali"],
      },
    ],
    areaServed: {
      "@type": "City",
      name: input.city,
      address: {
        "@type": "PostalAddress",
        addressRegion: input.region,
        addressCountry: input.countryCode,
      },
    },
    serviceType:
      input.serviceType ?? [
        "Local SEO",
        "Technical SEO",
        "E-commerce SEO",
        "Content Strategy",
        "Google Business Profile Optimization",
      ],
    provider: { "@id": `${SITE.url}/#person` },
    sameAs: Object.values(SITE.social),
  };
}
